import React from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { ListSupplierNotificationPreviewOutDTO, ShipmentStateCodeEnum, SupplierNotificationTypeEnum } from '@api/ringil-api.v2';
import { isNotBlank, isNotNullOrUndefined } from '@utils/utils';
import { dayjsFromApiPrague, dayjsFromApiUtc, stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import { formatTimeslotInterval, isSomeOpenTimeslotsExists } from '@utils/timeslotDateTime';
import { truncateFileName } from '@utils/fileUtils.ts';
import type { SecuredFlag } from '@components/security/types';
import SecuredArea from '@components/security/SecuredArea';
import EllipsisText from '@components/RingilComponents/EllipsisText';
import Badge from '@components/ringil3/Badges/Badge.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import CountryFlag from '@components/framework/CountryFlag/CountryFlag.tsx';
import ExpeditionBadge from '@components/RingilComponents/badges/ExpeditionBadge.tsx';
import GroupDemandBadge from '@components/RingilComponents/badges/GroupDemandBadge.tsx';
import ExpandableLabelWrapper from '@components/RingilDataTable/ExpandableLabelWrapper.tsx';
import ExpandableInfoAllTag from '@components/RingilDataTable/ExpandableInfoAllTag.tsx';
import ExpandableInfoTag from '@components/RingilDataTable/ExpandableInfoTag.tsx';
import AttachmentLink from '@components/RingilDataTable/AttachmentLink.tsx';
import type { FieldDefinition } from '@components/RingilDataTable/v3/columns/buildColumns';
import CalendarSvg from '@icons/calendar.svg?react';
import BuildingSvg from '@icons/BuildingSvg.tsx';
import WarningSvg from '@icons/Warning.tsx';
import AlertCircle from '@icons/AlertCircle';
import SvgChevron from '@icons/chevron.svg?react';
import SvgLink from '@icons/link.svg?react';
import SvgNumber from '@icons/number.svg?react';
import SvgFile from '@icons/file-inverted.svg?react';
import SvgCargo from '@icons/cargo.svg?react';
import { SnField as F, SnField, SnFieldGroup as Group, snInfoTagsCollapsible } from './snFieldKeys';

/**
 * The supplier notification list's field catalogue for RingilDataTable v3 (RD-2957).
 *
 * Every field is a piece of one of today's five cells, drawn with the component that cell uses, so
 * taking a cell apart never downgrades a value. The presets put the pieces back together and the
 * default tab is made of them. Test ids are the cells' own.
 */

/** The further-information tags whose expanded state the list remembers. */
export type SnInfoTag = 'all' | 'shipmentNumber' | 'cargo' | 'attachments' | 'internalNote';

export interface SnFieldContext {
  tr: (key: string, fallback: string, params?: Record<string, unknown>) => string;
  detailLink: (row: Row) => string;
  /** The logged-in user's company owns the notification (the customer side). */
  isOwner: (row: Row) => boolean;
  /** The user's timezone, for a group demand validity without its own. */
  timezoneId: string;
  flags: { snModule: SecuredFlag };
  infoTags: { isExpanded: (tag: SnInfoTag) => boolean; toggle: (tag: SnInfoTag) => void; setAll: (expanded: boolean) => void };
  labels: { expanded: boolean; toggle: () => void };
  onAttachmentClick: (attachmentId: string, fileName: string) => void;
}

type Row = ListSupplierNotificationPreviewOutDTO;

// Ringil 2.0 caption: 12/16, secondary — the second line of a cell.
const muted = 'text-xs leading-4 text-content-secondary';

const text = (value?: string | number | null, lines = 1) =>
  isNotNullOrUndefined(value) && value !== '' ? <EllipsisText lines={lines}>{String(value)}</EllipsisText> : null;

/** The shipment a row ends up on: a notification merged into another shipment follows the merge. */
const linkedExpeditionOf = (row: Row) => row.mergedToExpedition ?? row.expeditionCreated;

const location = (row: Row, side: 'loading' | 'unloading') => {
  const loading = side === 'loading';
  const name = loading ? row.supplierName : row.customerName;
  const country = loading ? row.supplierLocationCountry : row.customerLocationCountry;
  const city = loading ? row.supplierLocationCity : row.customerLocationCity;
  const testId = loading ? 'consignor' : 'consignee';
  if (!isNotBlank(name)) return null;
  return (
    <div className="flex min-w-0 flex-col">
      <div data-testid={`sn-row-${row.applicationId}-location-${testId}Name`}>
        <EllipsisText>{name}</EllipsisText>
      </div>
      <div className={clsx(muted, 'flex items-center gap-1')} data-testid={`sn-row-${row.applicationId}-location-${testId}Location`}>
        <CountryFlag code={country} />
        <EllipsisText>{[country, city].filter(Boolean).join(', ')}</EllipsisText>
      </div>
    </div>
  );
};

const timeslot = (row: Row, side: 'loading' | 'unloading') => {
  const slots = side === 'loading' ? row.supplierDeliverySlots : row.customerDeliverySlots;
  const testId = side === 'loading' ? 'consignor' : 'consignee';
  if (!(slots?.dayWithInterval?.length > 0)) return null;
  return (
    <div className="flex flex-col">
      <div className={clsx(muted, 'flex items-center gap-2')} data-testid={`sn-row-${row.applicationId}-location-${testId}Timeslot`}>
        <CalendarSvg className="shrink-0 text-content-secondary" /> {formatTimeslotInterval(slots)}
      </div>
      <COTimezone dataTestId={`sn-row-${row.applicationId}-location-${testId}Timezone`} timezoneId={slots.timezoneId} />
    </div>
  );
};

/** The open group demand of a notification without an expedition yet: its validity and the carriers' replies. */
const GroupDemandProgress: React.FC<{ row: Row; ctx: SnFieldContext }> = ({ row, ctx }) => {
  const { tr } = ctx;
  const gd = row.groupDemandCreated;
  const validity = (() => {
    if (!gd?.validTill || row.expeditionCreated?.expeditionApplicationId) return null;
    // Only use the new fields when both utcEnd and timezoneId are present.
    const hasNew = isNotNullOrUndefined(gd.utcEnd) && isNotNullOrUndefined(gd.timezoneId);
    const end = hasNew ? dayjsFromApiUtc(gd.utcEnd) : dayjsFromApiPrague(gd.validTill);
    const value = stringDateTimeWithLocaleFromApiStringInTz(hasNew ? gd.utcEnd : gd.validTill, hasNew ? gd.timezoneId : ctx.timezoneId);
    return end.isBefore(dayjs.utc()) ? (
      <div className="flex items-center gap-1 text-complementary-error">
        <AlertCircle width={16} height={16} />
        <span>
          {tr('SupplierNotificationList.expired', 'Expired')} {value}
        </span>
      </div>
    ) : (
      <div className="text-content-primary">
        {tr('SupplierNotificationList.validityTill', 'Validity till')} {value}
      </div>
    );
  })();
  const assigned = gd?.assignedCarriersCount ?? 0;
  const replied = gd?.repliedCarriersCount ?? 0;
  const percent = replied > 0 && assigned > 0 ? Math.min(100, (replied / assigned) * 100) : 10;
  return (
    <div className="flex flex-col gap-2 text-sm">
      {validity}
      {!assigned ? (
        <div className="flex items-center gap-1">
          <WarningSvg />
          <span>{tr('SupplierNotificationList.carriersAreNotAssigned', 'No requests sent to carriers')}</span>
        </div>
      ) : (
        <div className="flex w-full items-center gap-2 whitespace-nowrap">
          <span>
            {replied} {tr('SupplierNotificationList.responses', 'odpov.')}
          </span>
          <div className="relative h-2 w-[90px] shrink-0 overflow-hidden bg-content-quaternary">
            <div
              className={clsx('h-2 transition-[width] duration-300', replied > 0 ? 'bg-complementary-success' : 'bg-complementary-error')}
              style={{ width: `${percent}%` }}
            />
          </div>
          <span>
            {tr('SupplierNotificationList.from', 'z')} {assigned}
          </span>
        </div>
      )}
    </div>
  );
};

/** Builds the catalogue. Called on every render of the list, so each field sees the current expanded state — never cache it. */
export const buildSnFields = (ctx: SnFieldContext): FieldDefinition<Row>[] => {
  const fields = snFieldDefinitions(ctx);
  if (import.meta.env.DEV) {
    const missing = Object.values(SnField).filter(key => !fields.some(field => field.key === key));
    if (missing.length > 0) console.error('Supplier notification v3 catalogue: keys without a field definition', missing);
  }
  return fields;
};

const snFieldDefinitions = (ctx: SnFieldContext): FieldDefinition<Row>[] => {
  const { tr, infoTags } = ctx;
  const tag = (key: SnInfoTag) => ({ expanded: infoTags.isExpanded(key), onClick: () => infoTags.toggle(key) });

  return [
    // ------------------------------------------------------------------ notification
    {
      key: F.ApplicationId,
      group: Group.Notification,
      header: 'SupplierNotificationList.id',
      headerDefault: 'ID',
      sortField: 'applicationId',
      minWidth: 120,
      identity: true,
      required: true,
      render: row => (
        <>
          <span data-testid={`sn-row-${row.applicationId}`} data-application-id={row.applicationId} data-supplier-notification-id={row.id} style={{ display: 'none' }} />
          {/* data-row-open: in the split view a plain click opens the pane; Ctrl/Cmd-click still opens a new tab. */}
          <Link to={ctx.detailLink(row)} className="block min-w-0 font-medium" data-row-open>
            <EllipsisText>{row.applicationId}</EllipsisText>
          </Link>
        </>
      ),
    },
    {
      key: F.ResponsiblePerson,
      group: Group.Notification,
      header: 'generic.responsiblePerson',
      headerDefault: 'Responsible person',
      minWidth: 140,
      render: row => (isNotBlank(row.responsiblePersonName) ? <div className={muted}>{text(row.responsiblePersonName)}</div> : null),
    },
    {
      key: F.SnType,
      group: Group.Notification,
      header: 'SupplierNotificationList.type',
      headerDefault: 'Type',
      minWidth: 80,
      fitWidth: 70,
      render: row => (
        <div className="flex gap-1" data-testid={`sn-row-${row.applicationId}-snType`}>
          {row.supplierNotificationType === SupplierNotificationTypeEnum.DXX && <Badge label={tr('SupplierNotificationList.dxx', 'DXX')} />}
          {row.supplierNotificationType === SupplierNotificationTypeEnum.EFXX && <Badge label={tr('SupplierNotificationList.efxx', 'E/FXX')} />}
        </div>
      ),
    },
    {
      key: F.Labels,
      group: Group.Notification,
      header: 'generic.labels',
      headerDefault: 'Labels',
      minWidth: 160,
      render: row => (
        <SecuredArea id={`row-labels-${row.applicationId}`} visible={[{ type: 'data', label: 'labels.length > 0', satisfied: row.labels?.length > 0 }]} label="Labels">
          <ExpandableLabelWrapper labels={row.labels} isExpanded={ctx.labels.expanded} onToggle={ctx.labels.toggle} />
        </SecuredArea>
      ),
    },
    {
      key: F.LinkedExpedition,
      group: Group.Notification,
      header: 'SupplierNotificationList.expeditionAppId',
      headerDefault: 'Expedition',
      sortField: 'expeditionCreatedPreview.expeditionApplicationId',
      minWidth: 140,
      fitWidth: 130,
      render: row => {
        const linked = linkedExpeditionOf(row);
        if (!linked?.expeditionApplicationId) return null;
        // The badge links to the shipment, which needs the SN module; without it the id alone.
        return ctx.flags.snModule.granted ? (
          <SecuredArea id={`row-expedition-badge-${row.applicationId}`} visible={[ctx.flags.snModule]} label="Linked expedition badge">
            <ExpeditionBadge expeditionId={linked.expeditionId} expeditionAppId={linked.expeditionApplicationId} />
          </SecuredArea>
        ) : (
          text(linked.expeditionApplicationId)
        );
      },
    },

    // ------------------------------------------------------------------ expedition (state of the notification's transport)
    {
      key: F.WaitingForPayment,
      group: Group.Expedition,
      header: 'SupplierNotificationList.waitingForPayment',
      headerDefault: 'Waiting for payment',
      minWidth: 140,
      render: row => (
        <SecuredArea
          id={`row-waiting-payment-${row.applicationId}`}
          visible={[
            { type: 'ownership', label: 'logged user is entity owner', satisfied: ctx.isOwner(row) },
            { type: 'data', label: 'shipment state WAITING', satisfied: row.shipmentStateCode === ShipmentStateCodeEnum.WAITING },
          ]}
          label="Waiting for payment"
        >
          <div className="inline-flex items-center gap-1">
            <WarningSvg />
            <span>{row.shipmentStateName}</span>
          </div>
        </SecuredArea>
      ),
    },
    {
      key: F.WithoutTimeslots,
      group: Group.Expedition,
      header: 'SupplierNotificationList.withoutTimeslots',
      headerDefault: 'Without timeslots',
      minWidth: 140,
      render: row => (
        <SecuredArea
          id={`row-without-timeslots-${row.applicationId}`}
          visible={[
            { type: 'ownership', label: 'logged user is entity owner', satisfied: ctx.isOwner(row) },
            { type: 'data', label: 'no open supplier timeslots', satisfied: !isSomeOpenTimeslotsExists(row.supplierDeliverySlots) },
          ]}
          label="Without timeslots"
        >
          <div className="inline-flex items-center gap-1">
            <WarningSvg />
            <span>{tr('SupplierNotificationList.withoutTimeslots', 'Without timeslots')}</span>
          </div>
        </SecuredArea>
      ),
    },
    {
      key: F.GroupDemandProgress,
      group: Group.Expedition,
      header: 'SupplierNotificationList.groupDemandProgress',
      headerDefault: 'Carrier requests',
      minWidth: 200,
      fitWidth: 190,
      render: row => (
        <SecuredArea
          id={`row-group-demand-${row.applicationId}`}
          visible={[
            { type: 'ownership', label: 'logged user is entity owner', satisfied: ctx.isOwner(row) },
            { type: 'data', label: 'group demand created, no expedition yet', satisfied: !!row.groupDemandCreated && !row.expeditionCreated },
          ]}
          label="Group demand progress"
        >
          <GroupDemandProgress row={row} ctx={ctx} />
        </SecuredArea>
      ),
    },
    {
      key: F.GroupDemand,
      group: Group.Expedition,
      header: 'SupplierNotificationList.groupDemandConnectName',
      headerDefault: 'Group demand',
      minWidth: 140,
      render: row =>
        row.groupDemandCreated?.groupDemandName ? (
          <GroupDemandBadge groupDemandId={row.groupDemandCreated.groupDemandId} groupDemandName={row.groupDemandCreated.groupDemandName} linked={ctx.isOwner(row)} />
        ) : null,
    },

    // ------------------------------------------------------------------ loading / unloading
    {
      key: F.LoadingLocation,
      group: Group.Route,
      header: 'SupplierNotificationList.loadingLocation',
      headerDefault: 'Loading location',
      minWidth: 180,
      render: row => location(row, 'loading'),
    },
    {
      key: F.LoadingTimeslot,
      group: Group.Route,
      header: 'SupplierNotificationList.loadingTimeslot',
      headerDefault: 'Loading timeslot',
      minWidth: 180,
      render: row => timeslot(row, 'loading'),
    },
    {
      key: F.UnloadingLocation,
      group: Group.Route,
      header: 'SupplierNotificationList.unloadingLocation',
      headerDefault: 'Unloading location',
      minWidth: 180,
      render: row => location(row, 'unloading'),
    },
    {
      key: F.UnloadingTimeslot,
      group: Group.Route,
      header: 'SupplierNotificationList.unloadingTimeslot',
      headerDefault: 'Unloading timeslot',
      minWidth: 180,
      render: row => timeslot(row, 'unloading'),
    },

    // ------------------------------------------------------------------ supplier
    {
      key: F.Supplier,
      group: Group.Supplier,
      header: 'SupplierNotificationList.supplierName',
      headerDefault: 'Supplier',
      minWidth: 160,
      render: row =>
        isNotBlank(row.supplierName) ? (
          <div className="flex min-w-0 items-center gap-2" data-testid={`sn-row-${row.applicationId}-consignorName`}>
            <BuildingSvg />
            <EllipsisText>{row.supplierName}</EllipsisText>
          </div>
        ) : null,
    },
    {
      key: F.PartnerCompanyNote,
      group: Group.Supplier,
      header: 'SupplierNotificationList.partnerCompanyNote',
      headerDefault: 'Partner note',
      minWidth: 160,
      render: row => (isNotBlank(row.partnerCompanyNote) ? text(row.partnerCompanyNote, 3) : null),
    },

    // ------------------------------------------------------------------ further information (tags)
    {
      key: F.InfoExpandAll,
      group: Group.FurtherInformation,
      header: 'generic.expandAll',
      headerDefault: 'Expand all',
      inline: true,
      exportable: false,
      render: row => {
        const any = isNotBlank(row.shipmentNumber) || row.cargo?.length > 0 || row.attachmentInfo?.attachmentSize > 0 || isNotBlank(row.internalNote);
        if (!any) return null;
        const all = infoTags.isExpanded('all');
        return (
          <ExpandableInfoAllTag
            id={`all_${row.id}`}
            dataTestId={`sn-row-${row.applicationId}-additional-all`}
            onClick={() => infoTags.setAll(!all)}
            tooltipText={all ? tr('expandableInfoTag.collapseAll', 'Collapse all') : tr('expandableInfoTag.expandAll', 'Expand all')}
            icon={<SvgChevron className={clsx('text-content-brand', all && 'rotate-180')} />}
          />
        );
      },
    },
    {
      key: F.ShipmentNumber,
      group: Group.FurtherInformation,
      header: 'SupplierNotificationList.shipmentNumber',
      headerDefault: 'Shipment number',
      inline: true,
      render: (row, _toggleExpandedRow, cell) => {
        if (!isNotBlank(row.shipmentNumber)) return null;
        // Out of the further-information column: the value alone, as any other column shows it.
        if (!snInfoTagsCollapsible(cell.members)) return text(row.shipmentNumber);
        return (
          <ExpandableInfoTag
            id={`shipmentNumber_${row.id}`}
            dataTestId={`sn-row-${row.applicationId}-additional-shipmentNumber`}
            {...tag('shipmentNumber')}
            label={tr('SupplierNotificationList.shipmentNumber', 'Shipment')}
            text={row.shipmentNumber}
            icon={<SvgNumber />}
          />
        );
      },
    },
    {
      key: F.Cargo,
      group: Group.FurtherInformation,
      header: 'SupplierNotificationList.cargo',
      headerDefault: 'Cargo',
      inline: true,
      render: (row, _toggleExpandedRow, cell) => {
        if (!(row.cargo?.length > 0)) return null;
        const cargoText = row.cargo.map(c => `${c.name}: ${c.amount}`).join(', ');
        if (!snInfoTagsCollapsible(cell.members)) return text(cargoText, 3);
        return (
          <ExpandableInfoTag
            id={`cargo_${row.id}`}
            dataTestId={`sn-row-${row.applicationId}-additional-cargo`}
            {...tag('cargo')}
            label={tr('SupplierNotificationList.cargo', 'Cargo: ')}
            text={cargoText}
            icon={<SvgCargo />}
            count={row.cargo.reduce((sum, c) => sum + (c.amount ?? 0), 0)}
          />
        );
      },
    },
    {
      key: F.Attachments,
      group: Group.FurtherInformation,
      header: 'SupplierNotificationList.attachmentsHeader',
      headerDefault: 'Attachments',
      inline: true,
      render: (row, _toggleExpandedRow, cell) => {
        if (!(row.attachmentInfo?.attachmentSize > 0)) return null;
        const links = (
          <span className="font-medium">
            {row.attachmentInfo.attachments?.map((attachment, idx) => (
              <React.Fragment key={attachment.id}>
                <AttachmentLink id={attachment.id} name={attachment.name} onActivate={ctx.onAttachmentClick} maxLength={15} />
                {idx < row.attachmentInfo.attachments.length - 1 && <span className="text-content-tertiary">{' | '}</span>}
              </React.Fragment>
            ))}
          </span>
        );
        // The one value that does something: each file still downloads on click.
        if (!snInfoTagsCollapsible(cell.members)) return <div className="text-sm">{links}</div>;
        return (
          <ExpandableInfoTag
            id={`attachments_${row.id}`}
            dataTestId={`sn-row-${row.applicationId}-additional-attachments`}
            {...tag('attachments')}
            icon={<SvgLink className={infoTags.isExpanded('attachments') ? 'text-content-brand' : ''} />}
            label={tr('SupplierNotificationList.attachments', 'Attachments: ')}
            count={row.attachmentInfo.attachmentSize}
            text={row.attachmentInfo.attachments?.map(a => truncateFileName(a.name)).join(' | ')}
            textComponent={links}
          />
        );
      },
    },
    {
      key: F.InternalNote,
      group: Group.FurtherInformation,
      header: 'SupplierNotificationList.internalNoteHeader',
      headerDefault: 'Internal note',
      inline: true,
      render: (row, _toggleExpandedRow, cell) => {
        if (!isNotBlank(row.internalNote)) return null;
        if (!snInfoTagsCollapsible(cell.members)) return text(row.internalNote, 3);
        return (
          <ExpandableInfoTag
            id={`internalNote_${row.id}`}
            dataTestId={`sn-row-${row.applicationId}-additional-internalNote`}
            {...tag('internalNote')}
            label={tr('SupplierNotificationList.internalNote', 'Internal Note: ')}
            text={row.internalNote}
            icon={<SvgFile />}
          />
        );
      },
    },
  ];
};
